import * as api from "@/common/api";
import * as create from "./createApi";
import {
  LottoAccompanyCountResponse,
  LottoFavorite,
  LottoGame,
  LottoGameBuySummaryResponse,
  LottoNumberCountResponse,
  LottoResultResponse,
} from "../class";
import { DataResponse } from "@/common/class";

export const createApi = () => {
  return create;
};

//localhost:8241/api/v1/lotto/result/1132

/**
 * 로또 회차번호로 조회
 * @param drwNo 조회할 회차번호
 * @param errorAlert 없을시 오류메세지 출력 여부
 * @returns LottoResultResponse
 */
export const getLottoResult = async (
  drwNo: number,
  errorAlert = true
): Promise<LottoResultResponse> => {
  const res = await api.get(
    "/api/v1/lotto/result/" + drwNo,
    null,
    new api.HeaderOption(),
    errorAlert
  );
  console.log("getLottoResult res: ", res);
  const response = new LottoResultResponse();
  response.bind(res);
  return response;
};

/**
 * 구매내역 페이징 조회
 * @param id 유저 아이디
 * @param page 페이지 번호 (1부터)
 * @param size 페이지 사이즈
 * @returns { content, pageNumber, totalPages, totalElements, last }
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getLottoGameBuyList = async (id: string, page = 1, size = 20): Promise<any> => {
  const res = await api.get("/api/v1/lotto/game/buy/" + id, {
    page,
    size,
    sort: "createDatetime",
    order: "DESC",
    win: false,
  });
  return res.data;
};

/**
 * 로또 구매번호 생성 요청 - summary1 : 혼합 믹스
 * @returns LottoGameBuySummaryResponse
 */
export const getLottoGameBuySummary =
  async (): Promise<LottoGameBuySummaryResponse> => {
    const res = await api.get("/api/v1/lotto/game/buy/summary/1");
    console.log("getLottoGameBuySummary res : ", res);
    const response = new LottoGameBuySummaryResponse();
    response.bind(res);
    return response;
  };

/**
 * 마지막 진행된 게임 결과 조회
 * @returns 마지막 추첨완료된 게임 결과
 */
export const getLastLottoResult = async (): Promise<LottoResultResponse> => {
  const res = await api.get("/api/v1/lotto/result/last");
  console.log("getLastLottoResult res : ", res);
  const response = new LottoResultResponse();
  response.bind(res);
  return response;
};

/**
 * 번호 출현 빈도 조회
 * @param descSort 내림차순 여부 (true: 많이 나온 순, false: 적게 나온 순)
 * @returns LottoNumberCountResponse
 */
export const getNumberCount = async (
  descSort: boolean
): Promise<LottoNumberCountResponse> => {
  const res = await api.get("/api/v1/lotto/result/numberCount/" + descSort);
  const response = new LottoNumberCountResponse();
  response.bind(res);
  return response;
};

/**
 * 동반 출현 횟수 조회
 * @param descSort 내림차순 여부
 * @returns LottoAccompanyCountResponse
 */
export const getAccompanyCount = async (
  descSort: boolean
): Promise<LottoAccompanyCountResponse> => {
  const res = await api.get("/api/v1/lotto/result/accompanyCount/" + descSort);
  const response = new LottoAccompanyCountResponse();
  response.bind(res);
  return response;
};

/**
 * 당첨 게임 목록 조회 (공개 API)
 * @param page 페이지 번호
 * @param size 페이지 사이즈
 * @returns { content, pageNumber, totalPages, totalElements, last }
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getWinGameList = async (page = 1, size = 10): Promise<any> => {
  const res = await api.get("/api/v1/lotto/game/buy/win", {
    page,
    size,
    sort: "createDatetime",
    order: "DESC",
    win: true,
  });
  return res.data;
};

/**
 * 로또 구매요청
 * @param gameList LottoGame 배열
 * @returns DataResponse
 */
export const saveLottoGameBuyList = async (gameList: LottoGame[]) => {
  const res = await api.post("/api/v1/lotto/game/buy", { gameList });
  console.log("saveLottoGameBuyList res : ", res);
  const response = new DataResponse();
  response.bind(res);
  return response;
};

/**
 * 구매 유형(출처) 수정
 * @param id 게임 ID
 * @param buyType TERRA/AUTO/MANUAL/SEMI_AUTO
 * @returns DataResponse
 */
export const updateLottoGameBuyType = async (id: number, buyType: string) => {
  const res = await api.put("/api/v1/lotto/game/buy/" + id + "/type", { buyType });
  const response = new DataResponse();
  response.bind(res);
  return response;
};

/**
 * 구매내역 삭제
 * @param lottoGameIds 삭제할 게임 ID 배열
 * @returns DataResponse
 */
export const deleteLottoGameBuyList = async (lottoGameIds: number[]) => {
  const res = await api.remove("/api/v1/lotto/game/buy", { lottoGameIds });
  const response = new DataResponse();
  response.bind(res);
  return response;
};

/**
 * 전체 로또 결과 조회 (페이징)
 * @param page 페이지 번호
 * @param size 페이지 사이즈
 * @returns { content, pageNumber, totalPages, totalElements, last }
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getAllLottoResults = async (page = 1, size = 100): Promise<any> => {
  const res = await api.get("/api/v1/lotto/result", { page, size });
  return res.data;
};

/**
 * 즐겨찾기 목록 조회
 * @returns LottoFavorite[]
 */
export const getFavorites = async (): Promise<LottoFavorite[]> => {
  const res = await api.get("/api/v1/lotto/favorite");
  if (res.data && Array.isArray(res.data)) {
    return res.data.map((item: any) => {
      const fav = new LottoFavorite();
      fav.init(item);
      return fav;
    });
  }
  return [];
};

/**
 * 즐겨찾기 저장
 * @param favoriteName 즐겨찾기 이름
 * @param numbers 번호 6개 [n1, n2, n3, n4, n5, n6]
 * @returns DataResponse
 */
export const saveFavorite = async (favoriteName: string, numbers: number[]) => {
  const res = await api.post("/api/v1/lotto/favorite", {
    favoriteName,
    drwtNo1: numbers[0],
    drwtNo2: numbers[1],
    drwtNo3: numbers[2],
    drwtNo4: numbers[3],
    drwtNo5: numbers[4],
    drwtNo6: numbers[5],
  });
  const response = new DataResponse();
  response.bind(res);
  return response;
};

/**
 * 즐겨찾기 삭제
 * @param id 즐겨찾기 ID
 * @returns DataResponse
 */
export const deleteFavorite = async (id: number) => {
  const res = await api.remove("/api/v1/lotto/favorite/" + id, {});
  const response = new DataResponse();
  response.bind(res);
  return response;
};
